import { type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';
import { css } from '@emotion/css';
import { PollResultDisplayStyle } from '@soomo/lib/components/pageElements/PollQuestion/types';

import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { type RootState } from '@editor2/reducers/root';
import { findNearestDoubleElement } from '@editor2/services/doubleElementValidationsService';
import { elementCommonStyles } from '@editor2/styles/element';
import { type QuestionDeck, ElementType } from '@editor2/types/pageElements';

interface Props {
	element: QuestionDeck;
	courseId: number;
	pageId: string;
	index: number;
}

const QuestionDeckElement: FC<Props> = (props) => {
	const { element, courseId, pageId, index } = props;

	const { pages, elements } = useSelector((state: RootState) => state.courses[courseId]);

	const commonPayload = { elementId: element.family_id, courseId };
	const dispatch = useDispatch();

	const onChange = (value) => {
		dispatch(updateElement({ ...commonPayload, ...value }));
	};

	const pageElements = pages.entities[pageId].elements;
	const nearestDoubleElement = findNearestDoubleElement(pageElements, index, elements);
	const directionOutIndex = pageElements.indexOf(nearestDoubleElement.family_id);
	const elementsInside = pageElements.slice(index + 1, directionOutIndex);

	// if there is at least one Poll Alias in QuestionDeck
	const isPollAliasQuestionDeck = elementsInside.some(
		(elementFamilyId) => elements.entities[elementFamilyId].type === ElementType.PollResults
	);

	return (
		<Form className={elementCommonStyles}>
			{isPollAliasQuestionDeck && (
				<Form.Group>
					<Form.Label>Poll Alias Result display style:</Form.Label>
					<Form.Check
						type="radio"
						checked={element.poll_alias_result_display_style === PollResultDisplayStyle.COMPLETED}
						onChange={() =>
							onChange({ poll_alias_result_display_style: PollResultDisplayStyle.COMPLETED })
						}
						label="Show completed results"
					/>
					<Form.Text className={`text-muted ${optionExplanation}`}>
						Student can see results of any aliased poll they have answered
					</Form.Text>
					<Form.Check
						type="radio"
						checked={element.poll_alias_result_display_style === PollResultDisplayStyle.ALL}
						onChange={() =>
							onChange({ poll_alias_result_display_style: PollResultDisplayStyle.ALL })
						}
						label="All or nothing"
					/>
					<Form.Text className={`text-muted ${optionExplanation}`}>
						Student must answer all of the aliased polls before they see any results
					</Form.Text>
				</Form.Group>
			)}
		</Form>
	);
};

export default QuestionDeckElement;

const optionExplanation = css`
	margin: 0 0 10px 20px;
`;
